"use client";

import { c as _c } from "react/compiler-runtime";
import { AppleIcon, DiscordIcon, GitHubIcon, GoogleIcon, XIcon } from "@daveyplate/better-auth-ui";
import { CheckCircle2, Loader2 } from "lucide-react";
import type React from "react";
import { Button } from "@/components/ui/button";
export type OAuthProvider = "github" | "google" | "apple" | "discord" | "twitter";
const providerConfig: Record<OAuthProvider, {
  icon: React.ComponentType<{
    className?: string;
  }>;
  label: string;
}> = {
  github: {
    icon: GitHubIcon,
    label: "GitHub"
  },
  google: {
    icon: GoogleIcon,
    label: "Google"
  },
  apple: {
    icon: AppleIcon,
    label: "Apple"
  },
  discord: {
    icon: DiscordIcon,
    label: "Discord"
  },
  twitter: {
    icon: XIcon,
    label: "X (Twitter)"
  }
};
const googleOAuthEnabled = Boolean(process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID?.trim());
interface OAuthProviderButtonProps {
  provider: OAuthProvider;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isLinked?: boolean;
  label?: string;
  className?: string;
  mode?: "full" | "icon";
  title?: string;
}
export function OAuthProviderButton(t0) {
  const $ = _c(18);
  const {
    provider,
    onClick,
    isLoading: t1,
    isDisabled: t2,
    isLinked: t3,
    label,
    className,
    mode: t4,
    title
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isDisabled = t2 === undefined ? false : t2;
  const isLinked = t3 === undefined ? false : t3;
  const mode = t4 === undefined ? "full" : t4;
  const config = providerConfig[provider];
  const Icon = config.icon;
  const displayLabel = isLinked ? `${config.label} linked` : label ?? `Link ${config.label}`;
  const t5 = `relative ${className ?? (mode === "icon" ? "w-10" : "w-full justify-start gap-3")}`;
  const t6 = isDisabled || isLoading || isLinked;
  const t7 = mode === "icon" ? "icon" : "default";
  let t8;
  if ($[0] !== Icon || $[1] !== isLoading) {
    t8 = isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />;
    $[0] = Icon;
    $[1] = isLoading;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  let t9;
  if ($[3] !== isLinked) {
    t9 = isLinked && <CheckCircle2 aria-hidden={true} className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-background text-primary" />;
    $[3] = isLinked;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== displayLabel || $[6] !== mode) {
    t10 = mode === "icon" ? <span className="sr-only">{displayLabel}</span> : <span>{displayLabel}</span>;
    $[5] = displayLabel;
    $[6] = mode;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== displayLabel || $[9] !== onClick || $[10] !== t10 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9 || $[16] !== title) {
    t11 = <Button aria-label={displayLabel} className={t5} disabled={t6} onClick={onClick} size={t7} title={title} variant="outline">{t8}{t9}{t10}</Button>;
    $[8] = displayLabel;
    $[9] = onClick;
    $[10] = t10;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = title;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
}
interface OAuthProviderButtonsProps {
  providers: OAuthProvider[];
  onLink: (provider: OAuthProvider) => void;
  isLoading?: (provider: OAuthProvider) => boolean;
  isDisabled?: (provider: OAuthProvider) => boolean;
  /** Providers already linked to the account — rendered checked and disabled. */
  linkedProviders?: string[];
  getLabel?: (provider: OAuthProvider) => string;
  className?: string;
  buttonClassName?: string;
  buttonMode?: "full" | "icon";
  /** Provider to mark as the one used last on this device. */
  highlightProvider?: OAuthProvider | null;
  /** Accessible suffix and tooltip for the highlighted provider. */
  highlightLabel?: string;
}
export function OAuthProviderButtons(t0) {
  const $ = _c(24);
  const {
    providers,
    onLink,
    isLoading,
    isDisabled,
    linkedProviders,
    getLabel,
    className,
    buttonClassName,
    buttonMode: t1,
    highlightProvider,
    highlightLabel
  } = t0;
  const buttonMode = t1 === undefined ? "full" : t1;
  const t2 = className ?? "grid gap-3";
  let t3;
  if ($[0] !== buttonClassName || $[1] !== buttonMode || $[2] !== getLabel || $[3] !== highlightLabel || $[4] !== highlightProvider || $[5] !== isDisabled || $[6] !== isLoading || $[7] !== linkedProviders || $[8] !== onLink || $[9] !== providers) {
    let t4;
    if ($[11] !== buttonClassName || $[12] !== buttonMode || $[13] !== getLabel || $[14] !== highlightLabel || $[15] !== highlightProvider || $[16] !== isDisabled || $[17] !== isLoading || $[18] !== linkedProviders || $[19] !== onLink) {
      t4 = provider_0 => <OAuthProviderButton className={provider_0 === highlightProvider ? `${buttonClassName ?? ""} ring-2 ring-primary/60 ring-offset-2 ring-offset-card` : buttonClassName} isDisabled={isDisabled?.(provider_0)} isLinked={linkedProviders?.includes(provider_0)} isLoading={isLoading?.(provider_0)} key={provider_0} label={provider_0 === highlightProvider && highlightLabel ? `${getLabel?.(provider_0) ?? providerConfig[provider_0].label} (${highlightLabel})` : getLabel?.(provider_0)} mode={buttonMode} onClick={() => onLink(provider_0)} provider={provider_0} title={provider_0 === highlightProvider ? highlightLabel : undefined} />;
      $[11] = buttonClassName;
      $[12] = buttonMode;
      $[13] = getLabel;
      $[14] = highlightLabel;
      $[15] = highlightProvider;
      $[16] = isDisabled;
      $[17] = isLoading;
      $[18] = linkedProviders;
      $[19] = onLink;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    t3 = providers.filter(_temp).map(t4);
    $[0] = buttonClassName;
    $[1] = buttonMode;
    $[2] = getLabel;
    $[3] = highlightLabel;
    $[4] = highlightProvider;
    $[5] = isDisabled;
    $[6] = isLoading;
    $[7] = linkedProviders;
    $[8] = onLink;
    $[9] = providers;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[21] !== t2 || $[22] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  return t4;
}
function _temp(provider) {
  return provider !== "google" || googleOAuthEnabled;
}